import { useState, useEffect } from 'react'
import {
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material'
import { Modal, Button } from '@components/common'
import api from '@services/api'

const initialFormData = {
  name: '',
  code: '',
  floors: 1,
  address: '',
  description: '',
  is_active: true,
}

const BuildingForm = ({ open, onClose, building, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormData)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  const isEditing = Boolean(building)

  useEffect(() => {
    if (open) {
      if (building) {
        setFormData({
          name: building.name || '',
          code: building.code || '',
          floors: building.floors || 1,
          address: building.address || '',
          description: building.description || '',
          is_active: building.is_active !== false,
        })
      } else {
        setFormData(initialFormData)
      }
      setErrors({})
    }
  }, [open, building])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.code.trim()) newErrors.code = 'Code is required'
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validate()) return

    setLoading(true)
    try {
      if (isEditing) {
        await api.patch(`/facilities/buildings/${building.id}/`, formData)
      } else {
        await api.post('/facilities/buildings/', formData)
      }

      onSuccess?.()
      onClose()
    } catch (error) {
      console.error('Error saving building:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? 'Edit Building' : 'Add Building'}
      maxWidth="sm"
      actions={
        <>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Building Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={Boolean(errors.name)}
            helperText={errors.name}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Building Code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            error={Boolean(errors.code)}
            helperText={errors.code}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Number of Floors"
            name="floors"
            type="number"
            value={formData.floors}
            onChange={handleChange}
            inputProps={{ min: 1 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="is_active"
              value={formData.is_active}
              onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.value }))}
              label="Status"
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Grid>
      </Grid>
    </Modal>
  )
}

export default BuildingForm
